import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import NotFound from "@/pages/NotFound";
import { channels, getChannel } from "@/mocks/channels";
import { articlesByChannel } from "@/mocks/articles";
import { toneSoft, toneText } from "@/utils/display";

type SortKey = "latest" | "hot";

export default function ChannelPage() {
  const { slug = "" } = useParams();
  const channel = getChannel(slug);
  const [sort, setSort] = useState<SortKey>("latest");
  const [activeTag, setActiveTag] = useState<string>("全部");

  const list = useMemo(() => articlesByChannel(slug), [slug]);

  const tags = useMemo(
    () => ["全部", ...Array.from(new Set(list.flatMap((a) => a.tags)))],
    [list],
  );

  const visible = useMemo(() => {
    const filtered = activeTag === "全部" ? list : list.filter((a) => a.tags.includes(activeTag));
    return [...filtered].sort((a, b) =>
      sort === "hot" ? b.views - a.views : a.publishedAt < b.publishedAt ? 1 : -1,
    );
  }, [list, activeTag, sort]);

  useSeo({
    title: channel
      ? `${channel.name} - ${channel.tagline} | 深瞳档案`
      : "栏目未找到 | 深瞳档案",
    description: channel
      ? `${channel.name}栏目：${channel.description}`
      : "该栏目不存在或已调整。",
    keywords: channel ? `${channel.name},${channel.tagline},内幕揭秘,幕后真相` : "深瞳档案",
    path: `/channel/${slug}`,
    type: "website",
  });

  if (!channel) return <NotFound />;

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: channel.name,
      url: `https://www.shentongdangan.cn/channel/${channel.slug}`,
      inLanguage: "zh-CN",
      description: channel.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.shentongdangan.cn/" },
        { "@type": "ListItem", position: 2, name: "栏目总览", item: "https://www.shentongdangan.cn/channels" },
        {
          "@type": "ListItem",
          position: 3,
          name: channel.name,
          item: `https://www.shentongdangan.cn/channel/${channel.slug}`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${channel.name}档案列表`,
      itemListElement: visible.map((a, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: a.title,
        url: `https://www.shentongdangan.cn/article/${a.slug}`,
      })),
    },
  ];

  return (
    <>
      <JsonLd data={schema} />
      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page">
          <Breadcrumb
            items={[{ label: "首页", path: "/" }, { label: "栏目总览", path: "/channels" }, { label: channel.name }]}
          />
          <div className="flex flex-col gap-5 pb-10 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-md ${toneSoft[channel.tone]}`}>
                <i className={`${channel.icon} text-2xl`} aria-hidden="true" />
              </span>
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                  {channel.name}
                </h1>
                <p className={`mt-2 text-sm font-medium ${toneText[channel.tone]}`}>{channel.tagline}</p>
              </div>
            </div>
            <p className="max-w-xl text-sm leading-relaxed text-foreground-600">{channel.description}</p>
          </div>
        </div>
      </div>

      <div className="container-page py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-background-200 pb-4">
          <ul className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <li key={tag}>
                <button
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    activeTag === tag
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tag}
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setSort("latest")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                sort === "latest" ? "bg-background-50 font-medium text-foreground-900" : "text-foreground-600"
              }`}
            >
              最新收录
            </button>
            <button
              type="button"
              onClick={() => setSort("hot")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${
                sort === "hot" ? "bg-background-50 font-medium text-foreground-900" : "text-foreground-600"
              }`}
            >
              阅读最多
            </button>
          </div>
        </div>

        {visible.length === 0 ? (
          <p className="py-20 text-center text-sm text-foreground-500">该筛选条件下暂无档案。</p>
        ) : (
          <div className="mt-2">
            {visible.map((a) => (
              <ArticleCard key={a.slug} article={a} variant="row" />
            ))}
          </div>
        )}

        <section aria-labelledby="other-channels" className="mt-14">
          <h2 id="other-channels" className="font-heading text-lg font-bold text-foreground-950">
            其他栏目
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {channels
              .filter((c) => c.slug !== channel.slug)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/channel/${c.slug}`}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-3.5 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={c.icon} aria-hidden="true" />
                  {c.name}
                </Link>
              ))}
          </div>
        </section>
      </div>
    </>
  );
}